import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle } from "lucide-react";
import { dayKey, todayKey } from "@/lib/time";
import { useConfirm } from "@/lib/confirm";

const sel = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";
const MOVING = /en.?route|on.?board|picked|arriv|progress/i;
const vLabel = (v) => `${v.fleet_number} · ${v.year || ""} ${v.make || ""} ${v.model || ""}`.replace(/\s+/g, " ").trim();

// Give a driver a vehicle (or a vehicle a driver). Switching clears the driver's previous vehicle.
export default function VehicleAssignDialog({ driverId, vehicleId, open, onOpenChange, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pickV, setPickV] = useState("");
  const [pickD, setPickD] = useState("");
  const [moving, setMoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const ask = useConfirm();

  useEffect(() => {
    if (!open) return;
    setPickV(vehicleId || ""); setPickD(driverId || ""); setMoving(null);
    Promise.all([fleetApi.get("/vehicles"), fleetApi.get("/drivers")])
      .then(([v, d]) => {
        setVehicles(v.data || []); setDrivers(d.data || []);
        if (vehicleId && !driverId) setPickD((v.data || []).find((x) => x.id === vehicleId)?.current_driver_id || "");
      })
      .catch((e) => toast.error(fleetError(e, "Couldn't load vehicles.")));
  }, [open, driverId, vehicleId]);

  useEffect(() => {
    setMoving(null);
    if (!open || !pickD) return;
    fleetApi.get(`/trips?driver_id=${encodeURIComponent(pickD)}&limit=200`)
      .then((r) => setMoving((r.data || []).find((t) => MOVING.test(t.status || "") && t.scheduled_pickup && dayKey(t.scheduled_pickup) === todayKey()) || null)).catch(() => {});
  }, [open, pickD]);

  const name = (id) => { const d = drivers.find((x) => x.id === id); return d ? d.full_name || d.name : id; };
  const current = vehicles.filter((v) => pickD && v.current_driver_id === pickD);
  const target = vehicles.find((v) => v.id === pickV);
  const takenFrom = target?.current_driver_id && target.current_driver_id !== pickD ? target.current_driver_id : null;
  const unchanged = target && target.current_driver_id === pickD && current.length === 1;

  const log = (v, toId) => api.post("/vehicle-log", { vehicle_id: v.id, from_driver_id: v.current_driver_id || "" }).catch(() => {});

  const save = async () => {
    if (!pickD || !pickV) { toast.error("Pick a driver and a vehicle."); return; }
    if ((current.length || takenFrom) && !(await ask({ title: `Give ${target.fleet_number} to ${name(pickD)}?`,
      description: [current.length ? `${name(pickD)} stops driving ${current.map((v) => v.fleet_number).join(", ")}.` : "", takenFrom ? `${name(takenFrom)} will be left without a vehicle.` : ""].filter(Boolean).join(" "),
      confirmLabel: current.length ? "Switch vehicle" : "Assign vehicle" }))) return;
    setBusy(true);
    try {
      for (const v of current.filter((x) => x.id !== pickV)) { await fleetApi.put(`/vehicles/${v.id}`, { current_driver_id: "" }); await log(v, ""); }
      await fleetApi.put(`/vehicles/${pickV}`, { current_driver_id: pickD });
      await log(target, pickD);
      toast.success(`${name(pickD)} now drives ${target.fleet_number}.${takenFrom ? ` ${name(takenFrom)} no longer has a vehicle.` : ""}`);
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.status === 403 ? "Only the owner can change vehicle assignments right now." : fleetError(e, "Couldn't assign the vehicle.")); }
    finally { setBusy(false); }
  };

  const unassign = async () => {
    if (!(await ask({ title: `Remove ${name(pickD)}'s vehicle?`, description: "They'll have no vehicle until you assign one.", confirmLabel: "Remove vehicle", destructive: true }))) return;
    setBusy(true);
    try {
      for (const v of current.filter((x) => !vehicleId || x.id === vehicleId)) { await fleetApi.put(`/vehicles/${v.id}`, { current_driver_id: "" }); await log(v, ""); }
      toast.success(`${name(pickD)} no longer has a vehicle.`);
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(fleetError(e, "Couldn't remove the vehicle.")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white sm:max-w-lg" data-testid="vehicle-assign-dialog">
        <DialogHeader>
          <DialogTitle>{current.length ? "Switch vehicle" : "Assign vehicle"}</DialogTitle>
          <DialogDescription className="text-slate-400">The driver's app shows the new vehicle on its next refresh. Inspections and trips are logged against it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Driver</label>
            <select value={pickD} onChange={(e) => setPickD(e.target.value)} disabled={!!driverId} className={sel} data-testid="vehicle-assign-driver">
              <option value="">Select a driver…</option>
              {drivers.filter((d) => d.status !== "retired" || d.id === pickD).map((d) => <option key={d.id} value={d.id}>{d.full_name || d.name}{d.status === "retired" ? " (retired)" : ""}</option>)}
            </select>
            {pickD && <p className="mt-1 text-xs text-slate-500" data-testid="vehicle-assign-current">Current: {current.length ? current.map(vLabel).join(", ") : "no vehicle"}</p>}
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Vehicle</label>
            <select value={pickV} onChange={(e) => setPickV(e.target.value)} disabled={!!vehicleId} className={sel} data-testid="vehicle-assign-vehicle">
              <option value="">Select a vehicle…</option>
              {vehicles.filter((v) => v.status !== "retired").map((v) => (
                <option key={v.id} value={v.id}>
                  {vLabel(v)}{v.wheelchair_accessible ? " · ♿" : ""}{v.is_spare ? " · spare" : ""}{v.status !== "active" ? ` · ${v.status}` : ""}{v.current_driver_id ? ` · with ${name(v.current_driver_id)}` : " · free"}
                </option>
              ))}
            </select>
          </div>
          {pickD && drivers.find((d) => d.id === pickD)?.status === "retired" && <Warn testid="vehicle-assign-retired-warn">This driver is retired. Free the vehicle instead of assigning it to them.</Warn>}
          {target && target.status !== "active" && <Warn testid="vehicle-assign-status-warn">This vehicle is marked “{target.status}”. Make sure it's safe to drive.</Warn>}
          {takenFrom && <Warn testid="vehicle-assign-taken-warn">{target.fleet_number} is currently with {name(takenFrom)}. They will be left without a vehicle.</Warn>}
          {moving && <Warn testid="vehicle-assign-moving-warn">{name(pickD)} is on trip {moving.external_trip_id} ({moving.status}). Switch cars only when it's safe to stop.</Warn>}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {current.length > 0 ? (
            <Button variant="ghost" onClick={unassign} disabled={busy} className="text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="vehicle-assign-remove">Remove vehicle</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="vehicle-assign-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy || !pickD || !pickV || unchanged || drivers.find((d) => d.id === pickD)?.status === "retired"} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="vehicle-assign-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {current.length ? "Switch vehicle" : "Assign vehicle"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Warn = ({ children, testid }) => (
  <p className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid={testid}>
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{children}
  </p>
);
